import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getWricefDecision,
  getWricefDecisionSummary,
  listWricefDecisions,
  postWricefDecisionBulk,
  putWricefDecision,
  type WricefDecisionBulkUpdate,
  type WricefDecisionUpdate,
} from "@/api/wricef-decisions";
import { diagnosticKeys } from "@/hooks/useDiagnostic";

/*
  A decisão do arquiteto por objeto WRICEF, no detalhe do objeto (#1343, F2).

  Arquivo próprio, e não dentro de `useWricefCatalog.ts`: os testes do detalhe
  do objeto mockam aquele módulo inteiro, e um hook novo lá dentro sumiria
  nesses mocks.
*/

const all = (projectId: string) => ["wricef-decisions", projectId] as const;

export const wricefDecisionKeys = {
  all,
  item: (projectId: string, objName: string) => [...all(projectId), objName] as const,
  // O prefixo `all` cobre a lista e cada item: a gravação invalida os dois juntos.
  list: (projectId: string) => [...all(projectId), "_list"] as const,
  summary: (projectId: string) => [...all(projectId), "_summary"] as const,
};

/** A chave da lista do WRICEF Catalog, sem a consulta (`useWricefCatalog.ts`). */
const catalogListKey = (projectId: string) => ["wricef-catalog", projectId] as const;

/** A decisão de um objeto, lida quando a seção "Decisão" abre. */
export function useWricefDecision(projectId: string | undefined, objName: string | undefined) {
  return useQuery({
    queryKey: wricefDecisionKeys.item(projectId ?? "", objName ?? ""),
    queryFn: () => getWricefDecision(projectId!, objName!),
    enabled: !!projectId && !!objName,
    staleTime: 30_000,
    // O queryClient do app relê ao voltar à aba quando o dado tem mais de 30 s
    // (lib/queryClient.ts). Com `version` nova, essa releitura recomeçaria o
    // formulário pelo `key` e apagaria, sem aviso, o que o arquiteto digitou.
    // Com o 409 (F2b), uma leitura velha não grava por cima de ninguém, e
    // reabrir o objeto continua relendo.
    refetchOnWindowFocus: false,
  });
}

/** Todos os objetos entregáveis do projeto, para o drill-down do Diagnóstico (#1477). */
export function useWricefDecisionList(projectId: string | undefined) {
  return useQuery({
    queryKey: wricefDecisionKeys.list(projectId ?? ""),
    queryFn: () => listWricefDecisions(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/** Os totais por decisão do topo do WRICEF Catalog (#1500, F3). */
export function useWricefDecisionSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: wricefDecisionKeys.summary(projectId ?? ""),
    queryFn: () => getWricefDecisionSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

/**
 * Grava a decisão de um objeto.
 *
 * Sem atualização otimista, como em `useUpdateOrgValidation`: a tela mostra o
 * que o servidor gravou. A resposta do `PUT` já é o item relido, e vai direto
 * para o cache. O snapshot do Diagnóstico é invalidado para o card do Ato 1
 * (F5) contar a decisão nova sem esperar os 30 s do cache. No erro, relê o
 * item: 404 e 422 podem querer dizer que a tela estava desatualizada.
 */
export function useSaveWricefDecision(projectId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: WricefDecisionUpdate) => putWricefDecision(projectId!, body),
    onSuccess: (saved) => {
      client.setQueryData(wricefDecisionKeys.item(projectId ?? "", saved.obj_name), saved);
      // A lista do drill-down (#1477) relê junto com o card: a linha trocada muda
      // de filtro, e a contagem de cada decisão muda com ela. A grade e os totais
      // do WRICEF Catalog (#1500) também: sem isso, a coluna, os botões e o topo
      // ficam com a decisão velha até recarregar a página. O prefixo da grade
      // relê também a worklist e os componentes do objeto aberto, que são
      // leituras pequenas.
      return Promise.all([
        client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") }),
        client.invalidateQueries({ queryKey: wricefDecisionKeys.list(projectId ?? "") }),
        client.invalidateQueries({ queryKey: wricefDecisionKeys.summary(projectId ?? "") }),
        client.invalidateQueries({ queryKey: catalogListKey(projectId ?? "") }),
      ]);
    },
    onError: (_error, body) =>
      client.invalidateQueries({ queryKey: wricefDecisionKeys.item(projectId ?? "", body.obj_name) }),
  });
}

/**
 * Grava a mesma decisão em vários objetos, pela grade do WRICEF Catalog (#1501).
 *
 * A resposta traz só as contagens e os recusados, e não os itens: não há o que
 * pôr no cache, então tudo o que mostra a decisão relê. O prefixo `all` cobre a
 * lista do drill-down, os totais do topo e cada objeto já aberto; sem ele, o
 * detalhe de um objeto do lote abriria com a decisão velha. No erro não relê
 * nada: o lote é uma transação só, e o 422 dele não gravou ninguém.
 */
export function useSaveWricefDecisionBulk(projectId: string | undefined) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: WricefDecisionBulkUpdate) => postWricefDecisionBulk(projectId!, body),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") }),
        client.invalidateQueries({ queryKey: wricefDecisionKeys.all(projectId ?? "") }),
        client.invalidateQueries({ queryKey: catalogListKey(projectId ?? "") }),
      ]),
  });
}
